import { useState, useRef, useEffect } from 'react';

import { IMedia } from '../../../utils/models';
import useArrowNavigation from '../../../hooks/useArrowNavigation/useArrowNavigation';

const useCarousel = (media: IMedia[], onSlideChange?) => {
  const [[page, direction], setPage] = useState<[number, number]>([0, 0]);
  const [position, setPosition] = useState<number>(0);
  const [ariaAnnouncement, setAriaAnnouncement] = useState<string>('');
  const [shouldUseAnnouncement, setShouldUseAnnouncement] = useState<boolean>(
    false
  );
  const carouselStepsRefDesktop = useRef<HTMLDivElement | null>(null);
  const carouselStepsRefMobile = useRef<HTMLDivElement | null>(null);

  // allows tabs/steps to be controlled with arrow keys
  const {
    currentItemIndex: currentItemIndexDesktop,
    setCurrentItemIndex: setCurrentItemIndexDesktop,
  } = useArrowNavigation(carouselStepsRefDesktop, media.length);

  // allows tabs/steps to be controlled with arrow keys
  const {
    currentItemIndex: currentItemIndexMobile,
    setCurrentItemIndex: setCurrentItemIndexMobile,
  } = useArrowNavigation(carouselStepsRefMobile, media.length);

  /************
   * Controls *
   ************/

  // carousel previous and next buttons
  const paginate = (newDirection: number) => {
    const nextPage = (page + newDirection + media.length) % media.length;
    setPosition(-(nextPage * 100));

    if (shouldUseAnnouncement) {
      setAriaAnnouncement(
        `${media[nextPage].title} - ${nextPage + 1} of ${media.length}`
      );
    }

    setPage([nextPage, newDirection]);

    if (onSlideChange) {
      onSlideChange(page, nextPage);
    }
  };

  // stepper buttons at bottom of carousel
  const handleStepClick = (index: number) => {
    setPage(([prevPage]) => (prevPage < index ? [index, 1] : [index, -1]));
    setPosition(-(index * 100));
  };

  // keeps current item index in sync with the pagination
  useEffect(() => {
    setCurrentItemIndexDesktop(page);
    setCurrentItemIndexMobile(page);
  }, [page, setCurrentItemIndexDesktop, setCurrentItemIndexMobile]);

  /********************
   * Aria Live Region *
   ********************/

  // handles showing aria announcement when clicking next/prev buttons vs steps
  useEffect(() => {
    if (!shouldUseAnnouncement) {
      setAriaAnnouncement('');
    }
  }, [shouldUseAnnouncement, setAriaAnnouncement]);

  /***************
   * Swipe Logic *
   ***************/

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const onPanEnd = (_, info) => {
    const swipe = swipePower(info.offset.x, info.velocity.x);

    if (swipe < -swipeConfidenceThreshold) {
      paginate(1);
    } else if (swipe > swipeConfidenceThreshold) {
      paginate(-1);
    }
  };

  return {
    currentItemIndexDesktop,
    setCurrentItemIndexDesktop,
    currentItemIndexMobile,
    setCurrentItemIndexMobile,
    carouselStepsRefDesktop,
    carouselStepsRefMobile,
    page,
    direction,
    paginate,
    handleStepClick,
    ariaAnnouncement,
    setAriaAnnouncement,
    shouldUseAnnouncement,
    setShouldUseAnnouncement,
    position,
    onPanEnd,
  };
};

export default useCarousel;
